import { ChevronLeft, ChevronRight, Plus, Search, Settings2 } from 'lucide-react';
import { FormEvent, useCallback, useEffect, useState } from 'react';

import { useSessionUser } from '@/components/session-context';
import { EmptyState, LoadingRows, Modal, PageHeader, StatusBadge } from '@/components/ui';
import { apiFetch } from '@/lib/client-api';
import type { UserStatus } from '@/types';

const roles = ['FAN', 'PLAYER', 'TEAM_MANAGER', 'REFEREE', 'VENUE_OWNER', 'TOURNAMENT_ORGANIZER', 'FEDERATION', 'MAIN_ADMIN'] as const;
const inviteRoles = ['REFEREE', 'VENUE_OWNER', 'TOURNAMENT_ORGANIZER', 'FEDERATION', 'MAIN_ADMIN'] as const;
type UserRole = (typeof roles)[number];

interface User {
  id: string;
  email: string;
  phone: string | null;
  role: UserRole;
  status: UserStatus;
  createdAt: string;
  updatedAt: string;
  invitedBy: { email: string } | null;
}

interface Result {
  items: User[];
  pagination: { page: number; pageSize: number; total: number; totalPages: number };
}

export default function UsersPage() {
  const sessionUser = useSessionUser();
  const [result, setResult] = useState<Result>();
  const [search, setSearch] = useState('');
  const [role, setRole] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [error, setError] = useState('');
  const [invite, setInvite] = useState(false);
  const [editing, setEditing] = useState<User>();
  const [editRole, setEditRole] = useState<UserRole>('FAN');
  const [editStatus, setEditStatus] = useState<UserStatus>('ACTIVE');
  const [saving, setSaving] = useState(false);
  const [inviteResult, setInviteResult] = useState<{ email: string; inviteToken: string; expiresAt: string; role: string }>();

  const load = useCallback(() => {
    setError('');
    const query = new URLSearchParams({ page: String(page), pageSize: '25' });
    if (search) query.set('search', search);
    if (role) query.set('role', role);
    if (status) query.set('status', status);
    apiFetch<Result>(`/admin/users?${query}`).then(setResult).catch((reason: Error) => setError(reason.message));
  }, [page, role, search, status]);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  const openEditor = (user: User) => {
    setEditing(user);
    setEditRole(user.role);
    setEditStatus(user.status);
    setError('');
  };

  const saveUser = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editing || editing.id === sessionUser.id) return;
    const changes: { role?: UserRole; status?: UserStatus } = {};
    if (editRole !== editing.role) changes.role = editRole;
    if (editing.status !== 'PENDING_INVITE' && editStatus !== editing.status) changes.status = editStatus;
    if (Object.keys(changes).length === 0) {
      setEditing(undefined);
      return;
    }
    if (changes.status === 'SUSPENDED' && !confirm(`Suspend access for ${editing.email}?`)) return;
    setSaving(true);
    setError('');
    try {
      await apiFetch(`/admin/users/${editing.id}`, { method: 'PATCH', body: JSON.stringify(changes) });
      setEditing(undefined);
      load();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const submitInvite = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    try {
      const selectedRole = String(form.get('role'));
      const created = await apiFetch<{ email: string; inviteToken: string; expiresAt: string }>('/auth/invite', { method: 'POST', body: JSON.stringify({ email: form.get('email'), role: selectedRole }) });
      setInviteResult({ ...created, role: selectedRole });
      load();
    } catch (reason) {
      setError((reason as Error).message);
    }
  };

  return <div className="page">
    <PageHeader title="Users & access" description="Manage every account's role and access status, or invite a new privileged user." action={<button className="button" onClick={() => { setInvite(true); setInviteResult(undefined); }}><Plus size={18}/>Invite account</button>} />
    <div className="toolbar">
      <div style={{ position: 'relative', flex: '1 1 260px' }}><Search size={17} style={{ position: 'absolute', insetInlineStart: 13, top: 13, color: 'var(--muted)' }}/><input className="field search" style={{ paddingInlineStart: 40 }} value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} placeholder="Search email or phone" /></div>
      <select className="select" style={{ width: 210 }} value={role} onChange={(event) => { setRole(event.target.value); setPage(1); }}><option value="">All roles</option>{roles.map((item) => <option key={item}>{item}</option>)}</select>
      <select className="select" style={{ width: 180 }} value={status} onChange={(event) => { setStatus(event.target.value); setPage(1); }}><option value="">All statuses</option><option>ACTIVE</option><option>PENDING_INVITE</option><option>SUSPENDED</option></select>
    </div>
    {error && <div className="error" style={{ marginBottom: 14 }}>{error}</div>}
    {!result ? <LoadingRows /> : result.items.length === 0 ? <EmptyState /> : <>
      <div className="card table-wrap"><table className="table"><thead><tr><th>Account</th><th>Role</th><th>Status</th><th>Created</th><th>Invited by</th><th>Actions</th></tr></thead><tbody>{result.items.map((user) => {
        const isSelf = user.id === sessionUser.id;
        return <tr key={user.id}><td><strong>{user.email}</strong>{isSelf && <span className="badge" style={{ marginInlineStart: 8 }}>You</span>}<br/><span className="stat-label">{user.phone ?? 'Google account'}</span></td><td>{user.role.replaceAll('_', ' ')}</td><td><StatusBadge status={user.status}/></td><td>{new Date(user.createdAt).toLocaleDateString()}</td><td>{user.invitedBy?.email ?? 'Self-registered'}</td><td><button className="button secondary" disabled={isSelf} title={isSelf ? 'Your own admin role is protected' : 'Manage account'} onClick={() => openEditor(user)}><Settings2 size={16}/>Manage</button></td></tr>;
      })}</tbody></table></div>
      <div className="pagination"><span>{result.pagination.total} account{result.pagination.total === 1 ? '' : 's'}</span><div><button className="button secondary icon" disabled={page <= 1} onClick={() => setPage((value) => value - 1)} aria-label="Previous page"><ChevronLeft size={18}/></button><span>Page {result.pagination.page} of {Math.max(1, result.pagination.totalPages)}</span><button className="button secondary icon" disabled={page >= result.pagination.totalPages} onClick={() => setPage((value) => value + 1)} aria-label="Next page"><ChevronRight size={18}/></button></div></div>
    </>}
    {editing && <Modal title="Manage user account" onClose={() => !saving && setEditing(undefined)}><form className="form-grid" onSubmit={saveUser}>
      <div className="span-2 account-summary"><strong>{editing.email}</strong><span>Created {new Date(editing.createdAt).toLocaleString()}</span></div>
      <label className="form-field span-2"><span>Account role</span><select className="select" value={editRole} onChange={(event) => setEditRole(event.target.value as UserRole)}>{roles.map((item) => <option key={item} value={item}>{item.replaceAll('_', ' ')}</option>)}</select><small>Changing a role changes which management portal and features this user can access.</small></label>
      {editing.status === 'PENDING_INVITE'
        ? <div className="span-2 notice">This invitation has not been accepted yet. You can change its role, but access becomes active only after the user accepts it with the invited Google account.</div>
        : <label className="form-field span-2"><span>Access status</span><select className="select" value={editStatus} onChange={(event) => setEditStatus(event.target.value as UserStatus)}><option value="ACTIVE">Active</option><option value="SUSPENDED">Suspended</option></select><small>Suspended users cannot sign in or refresh their session.</small></label>}
      <div className="span-2 modal-actions"><button type="button" className="button secondary" disabled={saving} onClick={() => setEditing(undefined)}>Cancel</button><button className="button" disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button></div>
    </form></Modal>}
    {invite && <Modal title="Invite an elevated account" onClose={() => setInvite(false)}>{inviteResult ? <div><div className="badge active">Invite created</div><p><strong>{inviteResult.email}</strong></p><p style={{ color: 'var(--muted)' }}>Expires {new Date(inviteResult.expiresAt).toLocaleString()}</p><label className="form-field"><span>Google activation link</span><input className="field" readOnly value={`${inviteResult.role === 'VENUE_OWNER' ? (import.meta.env.VITE_OWNER_URL ?? 'https://owner.futsaly.com') : window.location.origin}/accept-invite?token=${inviteResult.inviteToken}`}/></label><p className="error">Send this single-use link securely. Email delivery is not configured yet.</p></div> : <form onSubmit={submitInvite} className="form-grid"><label className="form-field span-2"><span>Email</span><input className="field" type="email" name="email" required /></label><label className="form-field span-2"><span>Role</span><select className="select" name="role" required>{inviteRoles.map((item) => <option key={item} value={item}>{item.replaceAll('_', ' ')}</option>)}</select></label><div className="span-2 modal-actions"><button type="button" className="button secondary" onClick={() => setInvite(false)}>Cancel</button><button className="button">Create invite</button></div></form>}</Modal>}
  </div>;
}
